import React, { useEffect, useState, useCallback } from "react";
import {
  Image,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Flex,
  Box,
  Badge,
  Skeleton,
  Icon,
  Button,
  useToast,
} from "@chakra-ui/react";
import { FaCreditCard } from "react-icons/fa";
import AWS from "aws-sdk";
import { API, graphqlOperation } from "aws-amplify";
import { apiAuth } from "../../../Backend/api";
import { useSelector } from "react-redux";
import { Elements, CardNumberElement } from "@stripe/react-stripe-js";
import { CreditCardDrawer } from "../../../UI/Mini/creditCardDrawer";
import { useUpsellLogic } from "../../Logic/useUpsellLogic";
import { bottomDrawerSize } from "./drawerSizing";
import { getGuest } from "../../../graphql/queries";
import { isPropertyOnly } from "../../../Helper/propertyLink";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

const pricePreviewCache = {};

export const ServiceDrawer = ({ service, onClose, isOpen, booking, onComplete }) => {
  const toast = useToast();
  const [pricePreview, setPricePreview] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  // Per-tier previews, keyed "<serviceId>:<hours>" to match pricePreviewCache —
  // keying on hours alone would let two upsells that share a tier length show
  // each other's prices. The price-preview endpoint prices one tier per call,
  // so each duration button needs its own result to show the all-in total the
  // guest actually pays rather than the pre-fee base price.
  const [tierPreviews, setTierPreviews] = useState({});

  /* Hour tiers (Early Check-In / Late Check-Out upsells): one upsell can
   * offer several durations at different prices — the guest picks one
   * here and it drives both the charge and the access-code window. */
  // Memoized so the array identity is stable; the prefetch effect below
  // depends on it and would otherwise re-run on every render.
  const timeOptions = React.useMemo(
    () =>
      Array.isArray(service?.timeOptions) && service.timeOptions.length > 0
        ? [...service.timeOptions].sort((a, b) => a.hours - b.hours)
        : null,
    [service?.timeOptions]
  );
  const [selectedHours, setSelectedHours] = useState(null);

  // Default to the smallest (cheapest) tier whenever the drawer opens.
  useEffect(() => {
    if (isOpen && timeOptions) {
      setSelectedHours(timeOptions[0].hours);
    } else if (!isOpen) {
      setSelectedHours(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, service?.id]);

  /* Quantity range (count-based upsells like firewood bundles): the
   * guest picks how many and pays price × quantity. Mutually exclusive
   * with hour tiers by construction (the admin drawer can't set both). */
  const quantityCfg = React.useMemo(
    () =>
      !timeOptions &&
      service?.quantity &&
      Number.isInteger(service.quantity.min) &&
      Number.isInteger(service.quantity.max) &&
      service.quantity.min >= 1 &&
      service.quantity.max >= service.quantity.min
        ? service.quantity
        : null,
    [timeOptions, service?.quantity]
  );
  const [quantity, setQuantity] = useState(null);

  // Default to the minimum quantity whenever the drawer opens.
  useEffect(() => {
    if (isOpen && quantityCfg) {
      setQuantity(quantityCfg.min);
    } else if (!isOpen) {
      setQuantity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, service?.id]);

  const formatHours = (hours) =>
    `${hours} hour${Number(hours) === 1 ? "" : "s"}`;
  
  // Get manager data from Redux store and extract users from booking.owner
  const manager = useSelector((state) => {
    // Extract user ID from booking.owner (format: "userId::userId")
    const ownerId = booking?.owner ? booking.owner.split('::')[0] : null;
    
    return {
      email: state.user.email,
      password: state.user.password,
      stripeId: state.user.stripeId,
      users: ownerId, // Use the extracted owner ID from booking.owner
    };
  });

  const {
    stripePromise,
    imageUrls,
    loadingCard,
    setLoadingCard,
    isOpenPay,
    onOpenPay,
    onClosePay,
    renderedDescription,
  } = useUpsellLogic({ isOpen, booking, manager, upsell: service });

  const [guestEmail, setGuestEmail] = useState(null);

  useEffect(() => {
    if (!isOpen || !booking?.guestID) return;
    let cancelled = false;

    const loadGuest = async () => {
      try {
        const res = await API.graphql(
          graphqlOperation(getGuest, { id: booking.guestID })
        );
        if (cancelled) return;
        const email = res?.data?.getGuest?.email;
        if (email && !email.toLowerCase().includes("@noemail.com")) {
          setGuestEmail(email);
        } else {
          setGuestEmail(null);
        }
      } catch (err) {
        console.error("Failed to fetch guest email:", err);
      }
    };

    loadGuest();

    return () => {
      cancelled = true;
    };
  }, [isOpen, booking?.guestID]);

  const customHandleCardPayment = async ({
    stripe,
    elements,
    setLoading,
    toast,
    showUpsellPaidStep,
  }) => {
    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      toast({
        title: "Input Error",
        description: "Please fill in your card details.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    let bookingUpsellId = null;

    try {
      // Step 1: Authorize — creates a hold on the guest's card. For
      // hour-tier upsells the chosen duration prices the charge and is
      // stored on the purchase so the access window matches.
      const authRes = await apiAuth("/v2/upsells/payment/authorize", {
        bookingId: booking.id,
        upsellId: service.id,
        ...(timeOptions && selectedHours != null ? { selectedHours } : {}),
        ...(quantityCfg && quantity != null ? { quantity } : {}),
      });
      const { clientSecret } = authRes.data;
      bookingUpsellId = authRes.data.bookingUpsellId;

      // Step 2: Confirm the card with Stripe.js
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement },
        });

      if (confirmError) {
        throw new Error(confirmError.message || "Card declined. Please try again.");
      }

      // Step 3: Tell the backend the card is confirmed
      await apiAuth("/v2/upsells/payment/confirm-card", {
        bookingUpsellId,
        paymentIntentId: paymentIntent.id,
      });

      toast({
        title: "Request Sent",
        description: "We've contacted the service provider.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      showUpsellPaidStep?.();
      return true;
    } catch (error) {
      console.error("Payment error:", error);
      toast({
        title: "Payment Failed",
        description: error.message || "Something went wrong. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Guards against out-of-order preview responses when the guest clicks
  // through tiers quickly — only the latest request may write state.
  const previewSeqRef = React.useRef(0);

  // One request shape for every preview call, so the selected tier and the
  // sibling tiers prefetched below are always priced on identical terms.
  const buildPreviewBody = useCallback(
    (hours) => {
      const body = { upsellIds: [service?.id] };
      if (hours != null) body.selectedHours = hours;
      // Quantity-range upsells price at the chosen count (never combined
      // with hour tiers, so the sibling-tier prefetch is unaffected).
      if (quantityCfg && quantity != null) body.quantity = quantity;
      if (
        service?.saleType === "perNight" &&
        booking?.checkInDate &&
        booking?.checkOutDate
      ) {
        const nights = Math.ceil(
          (new Date(booking.checkOutDate) - new Date(booking.checkInDate)) /
            (1000 * 60 * 60 * 24)
        );
        if (nights > 0) body.nights = nights;
      }
      return body;
    },
    [service?.id, service?.saleType, booking?.checkInDate, booking?.checkOutDate, quantityCfg, quantity]
  );

  useEffect(() => {
    if (!isOpen || !service?.id) return;
    // Hour-tier upsells wait for the default tier selection before pricing.
    if (timeOptions && selectedHours == null) return;
    // Quantity-range upsells wait for the default quantity likewise.
    if (quantityCfg && quantity == null) return;

    // Cache per tier / per quantity — switching re-prices instantly on revisit.
    const cacheKey = timeOptions
      ? `${service.id}:${selectedHours}`
      : quantityCfg
        ? `${service.id}:q${quantity}`
        : service.id;
    if (pricePreviewCache[cacheKey]) {
      setPricePreview(pricePreviewCache[cacheKey]);
      setPriceLoading(false);
      return;
    }

    const seq = ++previewSeqRef.current;
    const fetchPreview = async () => {
      setPriceLoading(true);
      // Drop the previous tier's numbers immediately — a failed request
      // must fall back to the SELECTED tier's price, never silently keep
      // showing the old tier's total.
      setPricePreview(null);
      try {
        const res = await apiAuth(
          "/v2/upsells/payment/price-preview",
          buildPreviewBody(timeOptions && selectedHours != null ? selectedHours : null)
        );
        if (seq !== previewSeqRef.current) return; // stale response
        if (res.success && res.data?.items?.length > 0) {
          const item = res.data.items[0];
          setPricePreview(item);
          pricePreviewCache[cacheKey] = item;
          // Keep the selected tier's button in sync with its own all-in total.
          if (timeOptions && selectedHours != null) {
            setTierPreviews((prev) => ({ ...prev, [cacheKey]: item }));
          }
        }
      } catch (err) {
        console.error("Price preview fetch failed:", err);
      } finally {
        if (seq === previewSeqRef.current) {
          setPriceLoading(false);
        }
      }
    };
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, service?.id, selectedHours, quantity, booking?.checkInDate, booking?.checkOutDate]);

  // Price the *other* hour tiers in the background so every duration button
  // shows its real all-in total up front. The selected tier is owned by the
  // effect above, and anything already cached is skipped, so switching tiers
  // never refetches. Failures here are silent: the button just falls back to
  // the base price rather than blocking the drawer.
  useEffect(() => {
    if (!isOpen || !service?.id || !timeOptions) return undefined;

    const pending = timeOptions
      .map((o) => o.hours)
      .filter(
        (hours) =>
          hours !== selectedHours && !pricePreviewCache[`${service.id}:${hours}`]
      );
    if (pending.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        pending.map(async (hours) => {
          try {
            const res = await apiAuth(
              "/v2/upsells/payment/price-preview",
              buildPreviewBody(hours)
            );
            const item = res?.success ? res.data?.items?.[0] : null;
            if (item) pricePreviewCache[`${service.id}:${hours}`] = item;
            return [hours, item];
          } catch (err) {
            console.error(`Price preview failed for the ${hours}h tier:`, err);
            return [hours, null];
          }
        })
      );
      if (cancelled) return;
      setTierPreviews((prev) => {
        const next = { ...prev };
        results.forEach(([hours, item]) => {
          if (item) next[`${service.id}:${hours}`] = item;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, service?.id, timeOptions, selectedHours, buildPreviewBody]);

  if (!service) return null;

  // When the preview hasn't loaded (or failed), the fallback must track
  // the SELECTED tier / quantity, not the bare base price.
  const selectedOption =
    timeOptions && selectedHours != null
      ? timeOptions.find((o) => o.hours === selectedHours)
      : null;
  const unitFallback = selectedOption ? selectedOption.price : service?.price;
  const fallbackPrice =
    typeof unitFallback === "number" && quantityCfg && quantity
      ? unitFallback * quantity
      : unitFallback;

  const formattedPrice = pricePreview
    ? (pricePreview.total / 100).toLocaleString("en-CA", {
        style: "currency",
        currency: pricePreview.currency.toUpperCase(),
      })
    : fallbackPrice?.toLocaleString("en-CA", { style: "currency", currency: "CAD" });

  const currencyLabel = pricePreview
    ? pricePreview.currency.toUpperCase()
    : "CAD";

  // Linkbase service fee — always shown when we have the real breakdown
  // so the total is never a mystery number.
  const formatCents = (cents) =>
    ((cents || 0) / 100).toLocaleString("en-CA", {
      style: "currency",
      currency: (pricePreview?.currency || "cad").toUpperCase(),
    });

  // Resolve a tier's preview from whichever source has it: the live selected
  // preview, the prefetched map, or the module cache from a previous open.
  const tierPreviewFor = (hours) =>
    (hours === selectedHours ? pricePreview : null) ||
    tierPreviews[`${service.id}:${hours}`] ||
    pricePreviewCache[`${service.id}:${hours}`] ||
    null;

  // Duration buttons show the all-in total (base + service fee), matching what
  // the guest is charged. Until a tier's preview lands we fall back to the base
  // price so the button is never blank.
  const tierPriceLabel = (option) => {
    const preview = tierPreviewFor(option.hours);
    if (preview) {
      return (preview.total / 100).toLocaleString("en-CA", {
        style: "currency",
        currency: preview.currency.toUpperCase(),
      });
    }
    return Number(option.price).toLocaleString("en-CA", {
      style: "currency",
      currency: "CAD",
    });
  };

  return (
    <>
      {stripePromise && (
        <Elements stripe={stripePromise}>
          <Drawer placement="bottom" onClose={onClose} isOpen={isOpen} size="full">
            {/* No backdrop blur: blurring the whole page behind the sheet is
                GPU-heavy on phones and ran during the slide-in, which is a
                big part of why opening felt janky. The dim alone reads the
                same. */}
            <DrawerOverlay bg="blackAlpha.700" />
            <DrawerContent
              {...bottomDrawerSize({ mobile: 80, desktop: 85 })}
              borderTopRadius="20px"
              transition="filter 0.3s ease"
              filter={isOpenPay ? "brightness(0.6)" : "brightness(1)"}
              bg="#1A202C"
              maxW={{
                base: "100%",
                md: imageUrls.length > 0 && service.description ? "880px" : "600px",
              }}
              mx="auto"
              mb={{ md: 8 }}
              borderRadius={{ md: "24px" }}
            >
              {/* Drag handle — mobile affordance only */}
              <Flex justify="center" pt={3} pb={1} display={{ base: "flex", md: "none" }}>
                <Box w="36px" h="4px" borderRadius="full" bg="whiteAlpha.300" />
              </Flex>

              {/* Header */}
              <Flex px={{ base: 4, md: 6 }} pb={3} pt={{ base: 1, md: 5 }} alignItems="flex-start" justifyContent="space-between">
                <Box flex="1" pr={4}>
                  <Text fontSize={{ base: "20px", md: "24px" }} fontWeight={700} lineHeight="1.25" letterSpacing="-0.02em">
                    {service.name}
                  </Text>
                  {service.type && (
                    <Badge
                      mt={2}
                      colorScheme="purple"
                      variant="subtle"
                      fontSize="9px"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      textTransform="capitalize"
                    >
                      {service.type.toLowerCase().replace(/_/g, " ")}
                    </Badge>
                  )}
                </Box>
                <DrawerCloseButton
                  pos="relative"
                  top={0}
                  right={0}
                  borderRadius="full"
                  bg="whiteAlpha.100"
                  _hover={{ bg: "whiteAlpha.200" }}
                  size="sm"
                />
              </Flex>

              <DrawerBody px={0} pt={0} pb={4}>
                {/* Mobile: horizontal snap carousel above the text.
                    Desktop: images stack in a sticky left pane with the
                    description alongside — a proper two-pane dialog
                    instead of a stretched phone sheet. */}
                <Flex
                  direction={{ base: "column", md: "row" }}
                  align={{ md: "flex-start" }}
                  px={{ md: 6 }}
                  gap={{ md: 6 }}
                >
                  {imageUrls.length > 0 && (
                    <Box
                      pb={4}
                      w={{ base: "100%", md: service.description ? "45%" : "100%" }}
                      flexShrink={0}
                      position={{ md: "sticky" }}
                      top={{ md: 0 }}
                    >
                      <Flex
                        direction={{ base: "row", md: "column" }}
                        overflowX={{ base: "auto", md: "visible" }}
                        gap={3}
                        sx={{
                          "&::-webkit-scrollbar": { display: "none" },
                          scrollbarWidth: "none",
                          scrollSnapType: "x mandatory",
                        }}
                      >
                        {imageUrls.map((url, index) => (
                          <Box
                            key={index}
                            flexShrink={0}
                            scrollSnapAlign="start"
                            borderRadius="14px"
                            overflow="hidden"
                            ml={index === 0 ? { base: 4, md: 0 } : 0}
                            mr={index === imageUrls.length - 1 ? { base: 4, md: 0 } : 0}
                            w={
                              imageUrls.length === 1
                                ? { base: "calc(100% - 32px)", md: "100%" }
                                : { base: "80%", md: "100%" }
                            }
                            maxW={{ base: "320px", md: "none" }}
                          >
                            <Image
                              src={url}
                              h={{
                                base: "200px",
                                md: index === 0 ? "300px" : "150px",
                              }}
                              w="100%"
                              objectFit="cover"
                            />
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  )}

                  {/* Description */}
                  {service.description && (
                    <Box px={{ base: 4, md: 0 }} flex="1" minW={0}>
                    <Text
                      fontSize="11px"
                      fontWeight={600}
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      color="gray.500"
                      mb={2}
                    >
                      About this service
                    </Text>
                    <Box
                      fontSize="14px"
                      lineHeight="1.6"
                      color="gray.300"
                      sx={{
                        "& p": { mb: 2 },
                        "& a": { color: "#817EFF", textDecoration: "underline" },
                      }}
                      dangerouslySetInnerHTML={{ __html: renderedDescription }}
                    />
                  </Box>
                  )}
                </Flex>
              </DrawerBody>

              {/* Footer: Price + Pay */}
              {service.price > 0 && isPropertyOnly(booking) && (
                <DrawerFooter px={4} pb={6}>
                  <Text fontSize="14px" color="gray.500" textAlign="center" w="full">
                    Contact your host to add this to your stay.
                  </Text>
                </DrawerFooter>
              )}

              {service.price > 0 && booking && !isPropertyOnly(booking) && (
                <DrawerFooter
                  px={{ base: 4, md: 6 }}
                  pb={6}
                  pt={4}
                  flexDirection="column"
                  gap={3}
                  borderTop="1px solid"
                  borderColor="whiteAlpha.100"
                >
                  {/* Duration picker — hour-tier upsells only. The chosen
                      tier drives the price shown below and what gets
                      charged + granted. */}
                  {timeOptions && (
                    <Box w="full">
                      <Flex align="baseline" justify="space-between" mb={2}>
                        <Text
                          fontSize="11px"
                          fontWeight={600}
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color="gray.500"
                          textAlign="left"
                        >
                          Choose duration
                        </Text>
                      </Flex>
                      <Flex
                        gap={2}
                        overflowX="auto"
                        sx={{
                          "&::-webkit-scrollbar": { display: "none" },
                          scrollbarWidth: "none",
                        }}
                      >
                        {timeOptions.map((option) => {
                          const isSelected = selectedHours === option.hours;
                          return (
                            <Button
                              key={option.hours}
                              onClick={() => setSelectedHours(option.hours)}
                              variant="unstyled"
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                              h="56px"
                              minW="92px"
                              px={4}
                              borderRadius="12px"
                              border="1px solid"
                              bg={isSelected ? "rgba(129, 126, 252, 0.15)" : "whiteAlpha.50"}
                              borderColor={isSelected ? "#817efc" : "whiteAlpha.200"}
                              transition="all 0.15s ease"
                              _hover={{ borderColor: "#817efc" }}
                            >
                              <Text
                                fontSize="13px"
                                fontWeight={700}
                                color={isSelected ? "#a5a3ff" : "white"}
                                lineHeight="1.2"
                              >
                                {formatHours(option.hours)}
                              </Text>
                              <Text
                                fontSize="11px"
                                fontWeight={500}
                                color={isSelected ? "#a5a3ff" : "gray.400"}
                                lineHeight="1.2"
                              >
                                {tierPriceLabel(option)}
                              </Text>
                            </Button>
                          );
                        })}
                      </Flex>
                    </Box>
                  )}

                  {/* Quantity stepper — count-based upsells only. The total
                      below re-prices live as the count changes. */}
                  {quantityCfg && (
                    <Flex w="full" align="center" justify="space-between">
                      <Text
                        fontSize="11px"
                        fontWeight={600}
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                        color="gray.500"
                      >
                        Quantity
                      </Text>
                      <Flex
                        align="center"
                        gap={0}
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        borderRadius="12px"
                        overflow="hidden"
                      >
                        <Button
                          variant="unstyled"
                          h="40px"
                          w="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color={quantity > quantityCfg.min ? "white" : "whiteAlpha.300"}
                          isDisabled={quantity <= quantityCfg.min}
                          _hover={{ bg: "whiteAlpha.100" }}
                          onClick={() =>
                            setQuantity((q) => Math.max(quantityCfg.min, (q || quantityCfg.min) - 1))
                          }
                        >
                          −
                        </Button>
                        <Text
                          w="44px"
                          textAlign="center"
                          fontSize="15px"
                          fontWeight={700}
                          color="#a5a3ff"
                        >
                          {quantity ?? quantityCfg.min}
                        </Text>
                        <Button
                          variant="unstyled"
                          h="40px"
                          w="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color={quantity < quantityCfg.max ? "white" : "whiteAlpha.300"}
                          isDisabled={quantity >= quantityCfg.max}
                          _hover={{ bg: "whiteAlpha.100" }}
                          onClick={() =>
                            setQuantity((q) => Math.min(quantityCfg.max, (q || quantityCfg.min) + 1))
                          }
                        >
                          +
                        </Button>
                      </Flex>
                    </Flex>
                  )}

                  <Flex w="full" align="center" justify="space-between">
                    <Box>
                      {priceLoading ? (
                        <Skeleton height="28px" width="100px" borderRadius="6px" />
                      ) : (
                        <>
                          <Flex align="baseline" gap={1.5}>
                            <Text fontSize="24px" fontWeight={700} letterSpacing="-0.02em">
                              {formattedPrice}
                            </Text>
                            <Text fontSize="13px" fontWeight={500} color="gray.500">
                              {currencyLabel}
                            </Text>
                          </Flex>
                          {/* {pricePreview ? (
                            <Text fontSize="11px" fontWeight={500} color="gray.500" textAlign="left">
                              {formatCents(pricePreview.basePrice)}
                              {selectedOption ? ` for ${formatHours(selectedOption.hours)}` : ""}
                              {" + "}
                              {formatCents(pricePreview.platformFee)} service fee
                            </Text>
                          ) : (
                            <Text fontSize="11px" fontWeight={500} color="gray.500" textAlign="left">
                              + service fee at checkout
                            </Text>
                          )} */}
                        </>
                      )}
                    </Box>

                    <Button
                      bg="#817efc"
                      color="white"
                      borderRadius="12px"
                      fontSize="13px"
                      fontWeight={600}
                      h="44px"
                      px={6}
                      leftIcon={<FaCreditCard size={14} />}
                      isLoading={loadingCard}
                      _hover={{ bg: "#6e6be6" }}
                      _active={{ bg: "#5c59d4", transform: "scale(0.98)" }}
                      transition="all 0.15s ease"
                      onClick={() => {
                        if (!manager || !booking) return;
                        onOpenPay();
                      }}
                    >
                      Pay Now
                    </Button>
                  </Flex>

                  {/* Apple Pay disabled for now
                  <ApplePayUpsell
                    id={service.id}
                    titleDesc={service.name}
                    price={pricePreview ? pricePreview.total / 100 : calculateTotalPrice()}
                    booking={booking}
                    manager={manager}
                    onClose={onClose}
                    defaultCurrency={pricePreview?.currency?.toUpperCase() || "CAD"}
                    guestID={booking?.guestID}
                    bookingID={booking?.id}
                    owner={booking?.owner}
                  />
                  */}
                </DrawerFooter>
              )}

              {service.price > 0 && !booking && (
                <DrawerFooter px={4} pb={6}>
                  <Text fontSize="14px" color="gray.500" textAlign="center" w="full">
                    Booking information not available. Please contact support.
                  </Text>
                </DrawerFooter>
              )}
            </DrawerContent>
          </Drawer>

          <CreditCardDrawer
            isOpen={isOpenPay}
            onClose={onClosePay}
            handlePayment={customHandleCardPayment}
            upsellEmailFollowUp
            guestId={booking?.guestID}
            bookingId={booking?.id}
            defaultEmail={guestEmail}
            onUpsellComplete={onComplete || onClose}
            // Carry the chosen hour tier through to the card screen so the
            // guest can confirm the duration they're paying for. selectedOption
            // is null for non-tiered upsells, which hides the line entirely.
            itemName={service.name}
            itemDuration={
              selectedOption ? formatHours(selectedOption.hours) : null
            }
            itemAmount={priceLoading ? null : formattedPrice}
          />
        </Elements>
      )}
    </>
  );
}; 